/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import classNames from "classnames";
import { CustomLink } from "../custom-link";
import get from "lodash/get";

function getInlineStyle(inlineStyle, sectionColor) {
  if (inlineStyle) {
    if (sectionColor && (!inlineStyle.border && !inlineStyle.borderBottom)) {
      return Object.assign({}, inlineStyle, { borderBottom: `solid 2px ${sectionColor}` });
    }
  } else if (sectionColor) {
    return { borderBottom: `solid 2px ${sectionColor}` };
  }
  return inlineStyle;
}

/* Remove all the functions with 'dupe' in them after API update implementation by platform - https://github.com/quintype/itsman/issues/1450 */

function sectionPageRouteDupe(route, params) {
  return {
    pageType: "section-page",
    exact: true,
    sectionPath: route ? `/${route}` : route,
    params: params
  };
}

export function generateSectionPageRoutesDupe(config, opts = {}, currentSection = {}) {
  const sectionsById = config.sections.reduce((acc, section) => {
    acc[section.id] = section;
    return acc;
  }, {});
  const sectionId = get(currentSection, ["metadata", "section", 0, "id"]) || currentSection.id;
  const requiredSection = config.sections.find(section => section.id === sectionId) || {};
  return [requiredSection].flatMap(section => generateSectionPageRouteDupe(section, sectionsById, opts));
}

function addSectionPrefixDupe(route, params) {
  return sectionPageRouteDupe(`section/${route}`, params);
}

export function generateSectionPageRouteDupe(section, sectionsById, opts) {
  const params = { sectionId: section.id };
  if (section.collection) params.collectionSlug = section.collection.slug;

  let slug = section.slug;
  if (section["parent-id"]) {
    let currentSection = section;
    let depth = 0;
    while (currentSection["parent-id"] && depth++ < 5) {
      currentSection = sectionsById[currentSection["parent-id"]] || { slug: "invalid" };
      slug = `${currentSection.slug}/${slug}`;
    }
  }

  let routes = [];
  if (section["parent-id"] && opts.secWithoutParentPrefix) routes = [section.slug, slug];
  else routes = [slug];

  if (opts.addSectionPrefix)
    routes = routes.flatMap(route => [sectionPageRouteDupe(route, params), addSectionPrefixDupe(route, params)]);
  else routes = routes.flatMap(route => [sectionPageRouteDupe(route, params)]);

  return routes;
}

function getTemplate(props) {
  const sectionColor = props.sectionToColor(props.section);
  const inlineStyle = getInlineStyle(props.inlineStyle, sectionColor);
  const isSection = isSectionCollection();
  function isSectionCollection() {
    const template = get(props, ["section", "template"]);
    const section = get(props, ["section", "metadata", "section"], props.section);
    return template !== "collection" && template !== "default" && section;
  }

  if (isSection) {
    const sectionPageRoutes = generateSectionPageRoutesDupe(props.config, {}, props.section);
    const sectionPath = get(sectionPageRoutes, [0, "sectionPath"]);
    if (sectionPath) {
      return (
        <CustomLink
          href={sectionPath}
          className={classNames("section-name", "qt-theme__color--border", {
            "section-name--large": props.type === "large"
          })}
          style={inlineStyle}
        >
          {props.name}
        </CustomLink>
      );
    }
    return (
      <div
        className={classNames("section-name", "qt-theme__color--border", {
          "section-name--large": props.type === "large"
        })}
        style={inlineStyle}
      >
        {props.name}
      </div>
    );
  }

  if (get(props, ["section", "slug"])) {
    return (
      <CustomLink
        href={`/collection/${get(props, ["section", "slug"])}`}
        className={classNames("section-name", "qt-theme__color--border", {
          "section-name--large": props.type === "large"
        })}
        style={inlineStyle}
      >
        {props.name}
      </CustomLink>
    );
  }
  return (
    <div
      className={classNames("section-name", "qt-theme__color--border", {
        "section-name--large": props.type === "large"
      })}
      style={inlineStyle}
    >
      {props.name}
    </div>
  );
}

function CollectionNameBase(props) {
  return (
    <div data-section-name="true" className="section">
      {getTemplate(props)}
    </div>
  );
}

getTemplate.propTypes = {
  sectionToColor: PropTypes.func,
  section: PropTypes.Object,
  inlineStyle: PropTypes.Object,
  config: PropTypes.Object,
  type: PropTypes.string,
  name: PropTypes.string
};

function mapStateToProps(state) {
  return {
    config: state.qt.config || {},
    sectionToColor(section) {
      const defaultColor = get(state.qt.config, ["theme-attributes", "primary_color"], "#000000");
      if (section) {
        const menuItem = state.qt.config.layout.menu.find(
          item => item.type === "section" && section.id === item["item-id"]
        );
        return get(menuItem, ["data", "color"], defaultColor);
      }
      return defaultColor;
    }
  };
}

export const CollectionName = connect(mapStateToProps)(CollectionNameBase);
